import AddIcon from "@mui/icons-material/Add";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import { Box, Collapse, Fade, IconButton, type SxProps, Tooltip, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { DateTime } from "luxon";
import React, { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { useJobs } from "../../../hooks/useJobs";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { getWeekIndexOfSpecificDate, type WeeksAndActiveState } from "../../../pages/MasterSchedule/v2/useGetMasterScheduleWeeksAndActiveState";
import { INK, INK_BLACK, NEUTRAL, STATUS, STATUS_BADGE, SURFACE } from "../../../theme/tokens";
import { getWeekStart } from "../../../utils/dateUtils";
import { getEnabledNurseJobs, sortJobsByDisplayName } from "../../../utils/jobs";
import type { ConsecutiveDaysRecurring, Nurse, WeeklyRecurring } from "../../../utils/models";
import { getShiftIndexByShiftTimes, getUniqueLocations, inverseRolesMap } from "../../../utils/utilFunctions";
import { EditTimePopover } from "../../EditTimePopover";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";
import { type FlatSwitchOption, SystemFlatSwitch } from "../../UIToolkit/Clickable/SystemFlatSwitch";
import { DateInput } from "../../UIToolkit/Inputs/DateInput";
import { type DropdownItem, SystemDropdown } from "../../UIToolkit/Inputs/SystemDropDown";
import { DaysInWeek, DaysInWeekSelector } from "../util/DaysInWeekSelector";
import { RecurringTemplateIds } from "./RecurringTemplateIds";
import {
	defaultConsecutiveShiftConfig,
	getShiftTypeIconSx,
	type RecurringShiftMasterScheduleStateCreateMode,
	type RecurringShiftMasterScheduleStateEditMode,
	recurringShiftMasterScheduleRecurrenceSelectTypographySx,
	type SingleRecurringShift,
	SingleRecurringShiftStatus,
} from "./utils";

const dropdownSelectSx = {
	"& .MuiOutlinedInput-notchedOutline": { border: "none" },
	"& .MuiSelect-select": { py: 0, pl: 0, border: "none" },
	fontSize: { xs: "14px", md: "16px" },
	height: "24px",
} as const satisfies SxProps;

const getDropdownFormControlSx = (isOtherRecurrence: boolean, isJobManagementEnabled: boolean) =>
	({
		"& .MuiOutlinedInput-notchedOutline": { border: "none !important" },
		width: !isJobManagementEnabled
			? !isOtherRecurrence
				? { xs: "130px", xl: "200px", xxl: "250px" }
				: "250px"
			: !isOtherRecurrence
				? { xs: "62px", lg: "70px", xl: "130px", xxl: "160px" }
				: "180px",
		":hover ": { background: NEUTRAL[100], borderRadius: "4px" },
	}) as const satisfies SxProps;

const menuWidth = "200px" as const;

const getRecurrenceItems = (masterScheduleWeeks: number): Array<DropdownItem<number>> =>
	Array.from({ length: masterScheduleWeeks + 10 }, (_, i) => ({
		label: `${i + 1}`,
		value: i + 1,
		id: `recurrence-interval-${i + 1}`,
		disabled: i + 1 <= masterScheduleWeeks,
	}));

const consecutiveDaysAmountItems: Array<DropdownItem<number>> = Array.from({ length: 14 }, (_, i) => ({
	label: `${i + 1}`,
	value: i + 1,
	id: `consecutive-days-${i + 1}`,
}));

const recurrenceTypeOptions: FlatSwitchOption<"Weekly" | "ConsecutiveDays">[] = [
	{ label: "Weekly", value: "Weekly" },
	{ label: "Consecutive", value: "ConsecutiveDays" },
];

const actionIconsSx = { fontSize: { xs: "14px", xl: "18px" }, color: NEUTRAL[500] } as const satisfies SxProps;
const actionIconButtonSx = { p: { xs: 0.05, lg: 0.25 } } as const satisfies SxProps;

type Props = {
	shift: SingleRecurringShift;
	onChange: (_: { updatedShift: SingleRecurringShift; removed?: boolean; restored?: boolean }) => void;
	recurringGeneralState: RecurringShiftMasterScheduleStateCreateMode | RecurringShiftMasterScheduleStateEditMode;
	weekAndActiveState: WeeksAndActiveState;
	isOtherRecurrenceItem?: boolean;
	shouldScrollIntoViewOnMount?: boolean;
	shouldAnimateOnMount?: boolean;
	hasConflict?: boolean;
	credentialedLocationIds?: string[];
	allCredentialedLocationIds?: string[];
	/** Already flag-resolved by the panel — see `useRecurringShiftPanelWeekStartDay`. */
	weekStartDay: number;
	/** The staff member picked in the panel, from its roster. */
	selectedNurse: Nurse | undefined;
};

export const ShiftListItem: FC<Props> = ({
	onChange,
	recurringGeneralState,
	shift,
	weekAndActiveState,
	isOtherRecurrenceItem,
	shouldScrollIntoViewOnMount,
	shouldAnimateOnMount,
	hasConflict,
	credentialedLocationIds,
	allCredentialedLocationIds,
	weekStartDay,
	selectedNurse,
}) => {
	const rootRef = useRef<HTMLDivElement>(null);
	const { trackError } = useAnalytics();
	const isConsecutiveRecurringShiftsEnabled = useFeatureFlag(FEATURE_FLAGS.ConsecutiveRecurringShifts);
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const isRuntimeWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.ScheduleTemplateRuntimeWeekStart);
	const { chosenDepartment } = useChosenDepartment();
	const { timezone, isMedicalDepartment } = useDepartmentFlags();
	const enabledJobs = useJobs();
	const recurrenceItems = useMemo(() => getRecurrenceItems(chosenDepartment.master_schedule_weeks), [chosenDepartment.master_schedule_weeks]);
	const [timeEditingAnchor, setTimeEditingAnchor] = useState<null | HTMLElement>(null);
	const [expanded, setExpanded] = useState(isOtherRecurrenceItem || shift.startDate > weekAndActiveState.firstDate);

	const isConsecutiveDays = shift.recurrenceDetails.recurringType === "ConsecutiveDays";
	const isWeekly = shift.recurrenceDetails.recurringType === "Weekly";

	const uniqueLocations = useMemo<Array<DropdownItem<string>>>(() => {
		const allLocs = getUniqueLocations(chosenDepartment.locations, false);
		const credentialedLocs = credentialedLocationIds ? allLocs.filter((loc) => credentialedLocationIds.includes(loc.id)) : allLocs;
		const items = credentialedLocs.map<DropdownItem<string>>((loc) => ({ id: loc.id, label: loc.location, value: loc.location }));
		if (shift.location && !items.some((item) => item.value === shift.location)) {
			// If the shift's current location was filtered out, add it back as disabled so the dropdown can render the current value
			const existingLoc = allLocs.find((loc) => loc.location === shift.location);
			if (existingLoc) {
				items.push({ id: existingLoc.id, label: existingLoc.location, value: existingLoc.location, disabled: true });
			}
		}
		return items;
	}, [chosenDepartment.locations, credentialedLocationIds, shift.location]);

	const nurseJobs = useMemo(() => {
		if (!jobManagementEnabled || !selectedNurse) return [];
		return sortJobsByDisplayName(getEnabledNurseJobs(selectedNurse, enabledJobs));
	}, [jobManagementEnabled, selectedNurse, enabledJobs]);

	const isShiftTimesForDayShift = useMemo(() => {
		const start = DateTime.fromFormat(shift.startTime, "HH:mm");
		const end = DateTime.fromFormat(shift.endTime, "HH:mm");
		if (!start.isValid || !end.isValid) return true;
		return (
			getShiftIndexByShiftTimes({
				startTime: start,
				endTime: end,
			}) === 0
		);
	}, [shift.startTime, shift.endTime]);

	const editTimePickerClicked = useCallback((event: React.MouseEvent<HTMLElement>) => {
		setTimeEditingAnchor(event.currentTarget);
	}, []);

	const handleShiftChange = useCallback(
		(updatedShift: SingleRecurringShift, removed?: boolean, restored?: boolean) => {
			// Legacy (per-day OFF) keeps the clamp to preserve prior UX. Per-day ON skips it:
			// the clamp would falsely flip `userChangedApplyFrom` and rewrite per-day start_dates.
			const clampedStartDate = isRuntimeWeekStartEnabled
				? updatedShift.startDate
				: DateTime.max(updatedShift.startDate, getWeekStart(DateTime.now().setZone(timezone), weekStartDay));
			onChange({
				updatedShift: {
					...updatedShift,
					startDate: clampedStartDate,
					status:
						updatedShift.status === SingleRecurringShiftStatus.EXISTING && removed
							? SingleRecurringShiftStatus.TO_DELETE
							: updatedShift.status === SingleRecurringShiftStatus.EXISTING
								? SingleRecurringShiftStatus.EDITED
								: updatedShift.status,
				},
				removed,
				restored,
			});
		},
		[onChange, isRuntimeWeekStartEnabled, timezone, weekStartDay],
	);

	const handleJobChange = useCallback(
		(value: string | undefined) => {
			handleShiftChange({ ...shift, jobId: value });
		},
		[handleShiftChange, shift],
	);

	const handleTimeChange = useCallback(
		(newVals: { newStartTime: DateTime; newEndTime: DateTime }) => {
			handleShiftChange({
				...shift,
				startTime: newVals.newStartTime.toFormat("HH:mm"),
				endTime: newVals.newEndTime.toFormat("HH:mm"),
			});
		},
		[handleShiftChange, shift],
	);

	const closeTimePicker = useCallback(() => {
		setTimeEditingAnchor(null);
	}, []);

	const timeDisplayValue = useMemo<string>(() => {
		const start = DateTime.fromFormat(shift.startTime, "HH:mm");
		const end = DateTime.fromFormat(shift.endTime, "HH:mm");
		if (!start.isValid || !end.isValid) return "--:-- - --:--";
		return `${start.toFormat("h:mm a")} - ${end.toFormat("h:mm a")}`;
	}, [shift.startTime, shift.endTime]);

	const handleLocationChange = useCallback(
		(newLocation: string | undefined) => {
			handleShiftChange({
				...shift,
				location: newLocation,
			});
		},
		[handleShiftChange, shift],
	);

	const selectedLocation = useMemo<string | undefined>(() => {
		if (!shift.location) return undefined;
		// Prefer the value from uniqueLocations (covers both credentialed and the disabled fallback item),
		// but fall back to shift.location directly so existing shifts always display their location.
		return uniqueLocations.find((loc) => loc.value === shift.location)?.value ?? shift.location;
	}, [shift.location, uniqueLocations]);

	const isSelectedLocationNotCredentialed = useMemo(() => {
		if (!isMedicalDepartment || !allCredentialedLocationIds || !shift.location) return false;
		const allLocs = getUniqueLocations(chosenDepartment.locations, false);
		const selectedLoc = allLocs.find((loc) => loc.location === shift.location);
		return selectedLoc ? !allCredentialedLocationIds.includes(selectedLoc.id) : false;
	}, [isMedicalDepartment, allCredentialedLocationIds, shift.location, chosenDepartment.locations]);

	const handleRemoveShift = useCallback(() => {
		handleShiftChange(shift, true);
	}, [handleShiftChange, shift]);

	const shiftMarkedAsDeleted = useMemo(
		() => shift.status !== SingleRecurringShiftStatus.NEW && !!recurringGeneralState.existingShiftsToRemove.find((s) => s.id === shift.id),
		[shift.status, shift.id, recurringGeneralState.existingShiftsToRemove],
	);

	const handleSelectedDaysChange = useCallback(
		(selectedDays: DaysInWeek[]) => {
			if (selectedDays.length === 0) {
				handleRemoveShift();
				return;
			}
			const updatedRecurrenceDetails: WeeklyRecurring = {
				...shift.recurrenceDetails,
				weekdays: selectedDays.map((day) => day.toLowerCase()),
				recurringType: "Weekly",
				interval: shift.recurrenceDetails.interval,
			};
			handleShiftChange(
				{
					...shift,
					recurrenceDetails: updatedRecurrenceDetails,
				},
				false,
				/*If the shift is currently marked as deleted, and we toggled the days we retore it*/
				shiftMarkedAsDeleted,
			);
		},
		[handleShiftChange, shift, handleRemoveShift, shiftMarkedAsDeleted],
	);

	const handleExpandClick = useCallback(() => {
		setExpanded((prev) => !prev);
	}, []);

	const shouldDisableDate = useCallback(
		(day: DateTime) => {
			if (isOtherRecurrenceItem || isConsecutiveDays) return false;
			return (
				shift.recurrenceDetails.interval > 1 &&
				getWeekIndexOfSpecificDate({
					dateToCheck: day,
					chosenDepartmentFirstSunday: chosenDepartment.first_sunday,
					timezone,
					masterScheduleWeeks: shift.recurrenceDetails.interval,
					weekStartDay,
				}) !== weekAndActiveState.weekIndex
			);
		},
		[isOtherRecurrenceItem, isConsecutiveDays, chosenDepartment.first_sunday, timezone, weekAndActiveState.weekIndex, shift.recurrenceDetails, weekStartDay],
	);

	const handleDateStartChange = useCallback(
		(newDate: DateTime) => {
			handleShiftChange({
				...shift,
				startDate: newDate.startOf("day"),
			});
			if (shouldDisableDate(newDate)) {
				setTimeout(() => {
					handleShiftChange({
						...shift,
						startDate: weekAndActiveState.firstDate,
					});
				}, 300);
			}
		},
		[handleShiftChange, shift, shouldDisableDate, weekAndActiveState.firstDate],
	);

	const handleDateEndChange = useCallback(
		(newDate: DateTime) => {
			handleShiftChange({
				...shift,
				endDate: newDate.startOf("day"),
			});
			if (shouldDisableDate(newDate)) {
				setTimeout(() => {
					handleShiftChange({
						...shift,
						endDate: null,
					});
				}, 300);
			}
		},
		[handleShiftChange, shift, shouldDisableDate],
	);

	const handleClearEndDate = useCallback(() => {
		handleShiftChange({ ...shift, endDate: null });
	}, [handleShiftChange, shift]);

	const timeStart = useMemo(() => DateTime.fromFormat(shift.startTime, "HH:mm").setZone(timezone), [shift.startTime, timezone]);
	const timeEnd = useMemo(() => DateTime.fromFormat(shift.endTime, "HH:mm").setZone(timezone), [shift.endTime, timezone]);

	useEffect(() => {
		if (shouldScrollIntoViewOnMount && rootRef.current) {
			rootRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
		}
	}, [shouldScrollIntoViewOnMount]);

	const handleRecurrenceIntervalChange = useCallback(
		(newInterval: number | undefined) => {
			if (!newInterval || shift.recurrenceDetails.recurringType === "ConsecutiveDays") return;
			handleShiftChange({
				...shift,
				recurrenceDetails: {
					...shift.recurrenceDetails,
					interval: newInterval,
				},
			});
		},
		[handleShiftChange, shift],
	);

	const handleRecurrenceTypeSwitch = useCallback(
		(option: FlatSwitchOption<"Weekly" | "ConsecutiveDays">) => {
			if (option.value === shift.recurrenceDetails.recurringType) return;
			if (option.value === "ConsecutiveDays") {
				handleShiftChange({
					...shift,
					recurrenceDetails: defaultConsecutiveShiftConfig,
				});
			} else {
				handleShiftChange({
					...shift,
					recurrenceDetails: {
						recurringType: "Weekly",
						interval: chosenDepartment.master_schedule_weeks + 1,
						weekdays: [],
					},
				});
			}
		},
		[handleShiftChange, shift, chosenDepartment.master_schedule_weeks],
	);

	const handleConsecutiveWorkingDaysChange = useCallback(
		(value: number | undefined) => {
			if (!value || !isConsecutiveDays) return;
			handleShiftChange({
				...shift,
				recurrenceDetails: {
					...(shift.recurrenceDetails as ConsecutiveDaysRecurring),
					working_days_amount: value,
				},
			});
		},
		[handleShiftChange, shift, isConsecutiveDays],
	);

	const handleConsecutiveRestDaysChange = useCallback(
		(value: number | undefined) => {
			if (!value || !isConsecutiveDays) return;
			handleShiftChange({
				...shift,
				recurrenceDetails: {
					...(shift.recurrenceDetails as ConsecutiveDaysRecurring),
					rest_days_amount: value,
				},
			});
		},
		[handleShiftChange, shift, isConsecutiveDays],
	);

	if (!isWeekly && !isConsecutiveDays) {
		trackError("ShiftListItem_invalid_recurring_type", {
			shift: JSON.stringify(shift),
		});
		return null;
	}
	const consecutiveDisabled = isConsecutiveRecurringShiftsEnabled && shift.status !== SingleRecurringShiftStatus.NEW;

	return (
		<Box
			ref={rootRef}
			component={motion.div}
			initial={shouldAnimateOnMount ? { scale: 1 } : false}
			animate={shouldAnimateOnMount ? { scale: [0.7, 1] } : { scale: 1 }}
			transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
			onKeyDown={(e) => (shiftMarkedAsDeleted ? e.preventDefault() : undefined)}
			sx={[
				{
					display: "flex",
					flexDirection: "column",
					gap: 2,
					width: "100%",
					bgcolor: hasConflict ? STATUS.ERROR.light : NEUTRAL[50],
					borderRadius: "12px",
					p: 0.5,
					position: "relative",
				},
				shiftMarkedAsDeleted
					? {
							background: STATUS.ERROR.light,
							opacity: 0.5,
							"::after": {
								content: "''",
								position: "absolute",
								width: "100%",
								height: "2px",
								top: "50%",
								left: 0,
								backgroundColor: INK_BLACK,
								transform: "translateY(-50%)",
								pointerEvents: "none",
							},
						}
					: hasConflict
						? {
								border: `1px solid ${STATUS.ERROR.light}`,
							}
						: {},
			]}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: isOtherRecurrenceItem ? 3 : { xs: 1, xxl: 2 },
						flexWrap: "wrap",
					}}
				>
					<Box
						sx={{
							width: "12px",
							position: "relative",
							display: "flex",
							alignItems: "start",
							justifyContent: "start",
							height: "100%",
							mt: -1.5 /*Indicator can sit outside padding bounds*/,
						}}
					>
						<InHouseTooltip title={shift.status}>
							{shift.status === SingleRecurringShiftStatus.NEW ? (
								<AddIcon
									sx={{
										color: "green",
										fontSize: "16px",
									}}
								/>
							) : shiftMarkedAsDeleted ? (
								<ClearIcon
									color={"inherit"}
									fontSize={"inherit"}
									sx={{
										fontSize: "16px",
									}}
								/>
							) : shift.status === SingleRecurringShiftStatus.EDITED ? (
								<EditIcon
									sx={{
										fontSize: "12px",
									}}
								/>
							) : (
								<Box />
							)}
						</InHouseTooltip>
					</Box>
					{isWeekly ? (
						<DaysInWeekSelector
							selectedDays={
								shiftMarkedAsDeleted
									? []
									: (shift.recurrenceDetails as WeeklyRecurring).weekdays.map((day) => DaysInWeek[day.toLowerCase() as keyof typeof DaysInWeek])
							}
							onChange={handleSelectedDaysChange}
							gap={0.5}
							size="small"
							weekStartDay={weekStartDay}
						/>
					) : (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
							}}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 0.5,
								}}
							>
								<Typography sx={recurringShiftMasterScheduleRecurrenceSelectTypographySx}>Work</Typography>
								<SystemDropdown
									disabled={shiftMarkedAsDeleted}
									selectSx={{
										...recurringShiftMasterScheduleRecurrenceSelectTypographySx,
										height: recurringShiftMasterScheduleRecurrenceSelectTypographySx.fontSize,
										py: 1.5,
									}}
									selectedItem={(shift.recurrenceDetails as ConsecutiveDaysRecurring).working_days_amount}
									items={consecutiveDaysAmountItems}
									onChange={handleConsecutiveWorkingDaysChange}
								/>
								<Typography sx={recurringShiftMasterScheduleRecurrenceSelectTypographySx}>days</Typography>
							</Box>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 0.5,
								}}
							>
								<Typography sx={recurringShiftMasterScheduleRecurrenceSelectTypographySx}>Rest</Typography>
								<SystemDropdown
									disabled={shiftMarkedAsDeleted}
									selectSx={{
										...recurringShiftMasterScheduleRecurrenceSelectTypographySx,
										height: recurringShiftMasterScheduleRecurrenceSelectTypographySx.fontSize,
										py: 1.5,
									}}
									selectedItem={(shift.recurrenceDetails as ConsecutiveDaysRecurring).rest_days_amount}
									items={consecutiveDaysAmountItems}
									onChange={handleConsecutiveRestDaysChange}
								/>
								<Typography sx={recurringShiftMasterScheduleRecurrenceSelectTypographySx}>days</Typography>
							</Box>
						</Box>
					)}
					{!isMedicalDepartment && (
						<Box
							data-testid="recurring-shift-time-display"
							onClick={editTimePickerClicked}
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
								":hover ": { background: NEUTRAL[100], borderRadius: "4px" },
								cursor: "pointer",
								flexShrink: 0,
								...(shiftMarkedAsDeleted ? { pointerEvents: "none" } : {}),
							}}
						>
							<Typography sx={{ fontSize: { xs: "10px", lg: "12px" }, color: INK.SECONDARY, whiteSpace: "nowrap" }}>{timeDisplayValue}</Typography>
							{isShiftTimesForDayShift ? (
								<WbSunnyIcon sx={getShiftTypeIconSx(isShiftTimesForDayShift)} />
							) : (
								<BedtimeIcon sx={getShiftTypeIconSx(isShiftTimesForDayShift)} />
							)}
						</Box>
					)}
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
						}}
					>
						<Typography sx={{ fontSize: "10px", color: STATUS_BADGE.BRAND_FALLBACK, ml: 0.5 }}>Location</Typography>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.25,
							}}
						>
							<PlaceOutlinedIcon sx={{ fontSize: { xs: "16px", xxl: "22px" }, opacity: 0.7, color: STATUS_BADGE.BRAND_FALLBACK }} />
							<SystemDropdown
								data-testid="recurring-shift-location-select"
								disabled={shiftMarkedAsDeleted}
								renderValue={(selected) => (
									<Typography
										sx={{
											display: "flex",
											alignItems: "center",
											overflow: "hidden",
											fontSize: { xs: "12px", md: "13px" },
											color: INK.SECONDARY,
											lineHeight: "100%",
											pl: 0.5,
											height: dropdownSelectSx.height,
											textOverflow: "ellipsis",
											textWrap: "nowrap",
										}}
									>
										{selected ?? "Select Location"}
									</Typography>
								)}
								selectSx={dropdownSelectSx}
								formControlSx={getDropdownFormControlSx(isOtherRecurrenceItem ?? false, jobManagementEnabled)}
								items={uniqueLocations}
								onChange={handleLocationChange}
								selectedItem={selectedLocation}
								menuWidth={menuWidth}
							/>
							{isSelectedLocationNotCredentialed && (
								<InHouseTooltip title={"Not credentialed for this facility"}>
									<WarningAmberOutlined sx={{ fontSize: "16px", color: STATUS.ERROR.main }} />
								</InHouseTooltip>
							)}
						</Box>
					</Box>
					{nurseJobs.length > 0 && (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
							}}
						>
							<Typography sx={{ fontSize: "10px", color: STATUS_BADGE.BRAND_FALLBACK, ml: 0.5 }}>Job</Typography>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 0.25,
								}}
							>
								<SystemDropdown
									disabled={shiftMarkedAsDeleted}
									renderValue={(selected) => (
										<Typography
											sx={{
												display: "flex",
												alignItems: "center",
												overflow: "hidden",
												fontSize: { xs: "12px", md: "13px" },
												color: INK.SECONDARY,
												lineHeight: "100%",
												pl: 0.5,
												height: dropdownSelectSx.height,
												textOverflow: "ellipsis",
												textWrap: "nowrap",
											}}
										>
											{nurseJobs.find((j) => j.id === selected)?.displayName ?? "Select Job"}
										</Typography>
									)}
									selectSx={dropdownSelectSx}
									formControlSx={getDropdownFormControlSx(isOtherRecurrenceItem ?? false, jobManagementEnabled)}
									items={nurseJobs.map((j) => ({ id: j.id, label: j.displayName, value: j.id }))}
									onChange={handleJobChange}
									selectedItem={shift.jobId ?? ""}
									menuWidth={menuWidth}
								/>
							</Box>
						</Box>
					)}
				</Box>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: { xs: 0.25, xl: 0.5 },
					}}
				>
					{isOtherRecurrenceItem && isConsecutiveRecurringShiftsEnabled && (
						<Tooltip title={consecutiveDisabled ? "Recurrence Type cannot be edited after creation" : undefined}>
							<Box
								sx={{
									opacity: consecutiveDisabled ? 0.5 : 1,
								}}
							>
								<SystemFlatSwitch
									options={recurrenceTypeOptions.map((opt) => ({
										...opt,
										disabled: consecutiveDisabled,
									}))}
									activeIndexOrValue={shift.recurrenceDetails.recurringType !== "Daily" ? shift.recurrenceDetails.recurringType : 0}
									onSwitch={handleRecurrenceTypeSwitch}
								/>
							</Box>
						</Tooltip>
					)}
					<IconButton
						data-testid={`recurring-shift-trash-${shift.id ?? "new"}`}
						disabled={shiftMarkedAsDeleted}
						onClick={handleRemoveShift}
						size={"small"}
						sx={actionIconButtonSx}
					>
						<DeleteOutlineIcon sx={actionIconsSx} />
					</IconButton>
					<IconButton disabled={shiftMarkedAsDeleted} onClick={handleExpandClick} size="small" sx={actionIconButtonSx}>
						{expanded ? <ExpandLess sx={actionIconsSx} /> : <ExpandMore sx={actionIconsSx} />}
					</IconButton>
				</Box>
			</Box>
			<Collapse in={expanded} timeout="auto" unmountOnExit>
				<Fade in={expanded} timeout={500}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "start",
							flexWrap: "wrap",
							gap: 2,
							px: 1,
							pb: 1,
						}}
					>
						<DateInput
							minDate={
								shift.originalStartDate
									? DateTime.max(shift.originalStartDate, getWeekStart(DateTime.now().setZone(timezone), weekStartDay))
									: getWeekStart(DateTime.now().setZone(timezone), weekStartDay)
							}
							maxDate={selectedNurse?.scheduledDeactivationDate ?? undefined}
							onChange={handleDateStartChange}
							value={shift.startDate}
							label={"Apply From"}
							shouldDisableDate={shouldDisableDate}
							fullHeight={true}
							fontSx={{ fontSize: "12px" }}
							inputSx={{ backgroundColor: SURFACE, ":hover": { backgroundColor: NEUTRAL[100] } }}
						/>
						<DateInput
							minDate={shift.startDate}
							maxDate={selectedNurse?.scheduledDeactivationDate ?? undefined}
							onChange={handleDateEndChange}
							value={shift.endDate ?? undefined}
							label={"End Date"}
							fullHeight={true}
							shouldDisableDate={shouldDisableDate}
							fontSx={{ fontSize: "12px" }}
							inputSx={{ backgroundColor: SURFACE, ":hover": { backgroundColor: NEUTRAL[100] } }}
							onClear={handleClearEndDate}
						/>
						{isWeekly && (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: 0.5,
								}}
							>
								<Typography sx={recurringShiftMasterScheduleRecurrenceSelectTypographySx}>Repeats every</Typography>
								{!isOtherRecurrenceItem ? (
									<Typography sx={recurringShiftMasterScheduleRecurrenceSelectTypographySx}>{shift.recurrenceDetails.interval}</Typography>
								) : (
									<SystemDropdown
										disabled={!isOtherRecurrenceItem}
										selectSx={{
											...recurringShiftMasterScheduleRecurrenceSelectTypographySx,
											height: recurringShiftMasterScheduleRecurrenceSelectTypographySx.fontSize,
											py: 1.5,
										}}
										selectedItem={shift.recurrenceDetails.interval}
										items={recurrenceItems}
										onChange={handleRecurrenceIntervalChange}
									/>
								)}
								<Typography sx={recurringShiftMasterScheduleRecurrenceSelectTypographySx}>week{shift.recurrenceDetails.interval === 1 ? "" : "s"}</Typography>
							</Box>
						)}
						<RecurringTemplateIds shift={shift} weekStartDay={weekStartDay} />
					</Box>
				</Fade>
			</Collapse>
			{timeEditingAnchor && !isMedicalDepartment && (
				<EditTimePopover
					role={selectedNurse ? inverseRolesMap[selectedNurse.role] : undefined}
					jobId={shift.jobId}
					onTimeChange={handleTimeChange}
					onClose={closeTimePicker}
					anchorEl={timeEditingAnchor}
					startTime={timeStart}
					endTime={timeEnd}
				/>
			)}
		</Box>
	);
};
